import { crownsForRank } from '@shared/seasons';
import { CROWN_GLYPH } from '../../components/currencyGlyphs';
import { leaderboardDisplayName } from '../../components/leaderboardName';
import type { LeaderboardEntry } from '../../apiClient/maps';

export interface LeaderboardPanelProps {
  entries: ReadonlyArray<LeaderboardEntry>;
  collapsed: boolean;
  onToggle: () => void;
  /**
   * The current player's UID, used to highlight their own row in the
   * leaderboard. Pass `undefined` for signed-out viewers.
   */
  selfUid?: string;
  /** How many entries to display. Defaults to 5 (the top of the chart). */
  maxRows?: number;
  competitiveEligible: boolean;
}

/**
 * Top-N leaderboard side panel rendered alongside the play canvas.
 * Mode-agnostic — leaderboard entries share the same shape regardless
 * of gameplay system, and the highlight-self / collapse affordances
 * are identical between Pulse and Circles.
 */
export function LeaderboardPanel({
  entries,
  collapsed,
  onToggle,
  selfUid,
  maxRows = 5,
  competitiveEligible,
}: LeaderboardPanelProps) {
  return (
    <aside
      className={`play-leaderboard ${collapsed ? 'collapsed' : ''}`}
      data-testid="leaderboard"
    >
      <button
        type="button"
        className="play-leaderboard-toggle"
        onClick={onToggle}
        aria-label={collapsed ? 'Show leaderboard' : 'Hide leaderboard'}
        aria-pressed={!collapsed}
        data-testid="leaderboard-toggle"
      >
        {collapsed ? '\u2039' : '\u203A'}
      </button>
      <h3>Top runs</h3>
      {!competitiveEligible && <p className="muted faint">Map records only · no season rewards</p>}
      <div className="play-leaderboard-list">
        {entries.length === 0 ? (
          <p className="muted faint" data-testid="empty-leaderboard">
            No verified clears yet. Be the first!
          </p>
        ) : (
          <ol>
            {entries.slice(0, maxRows).map((entry, i) => {
              // Podium rows hold season crowns (#1 → 3, #2 → 2, #3 → 1); show
              // them as a tiny addon so the per-map board doubles as a crown
              // tracker. Rows below the podium earn none and render no badge.
              const crowns = competitiveEligible ? crownsForRank(i + 1) : 0;
              return (
                <li
                  key={entry.runId}
                  className={selfUid === entry.userUid ? 'lb-self' : ''}
                >
                  <span className="lb-rank">#{i + 1}</span>
                  <span className="lb-name">{leaderboardDisplayName(entry)}</span>
                  <span
                    className="lb-crown"
                    title={crowns > 0 ? `${crowns} season crown${crowns > 1 ? 's' : ''}` : undefined}
                  >
                    {crowns > 0 ? `${CROWN_GLYPH}${crowns}` : ''}
                  </span>
                  <span className="lb-score">{entry.score.toLocaleString()}</span>
                  <span className="lb-acc">{(entry.accuracy * 100).toFixed(0)}%</span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </aside>
  );
}
